import type { LastClean } from '@shared/schemas';

import type {
  BrokenUrlProfile,
  DuplicateGroup,
  LoadStatus,
  MixedCurrencyExitProfile,
} from '../domain/profile-cleaning.model';

/**
 * Transient result of the last settled operation (clean / merge). The page
 * turns `success` into a success snackbar and each `errors` entry into an error
 * snackbar, then clears it. A single bucket is enough because the action buttons
 * are disabled while any operation is in flight.
 */
export interface Notice {
  success: string | null;
  errors: string[];
}

/**
 * Redux state for the admin Profile Cleaning page. The slice owns the two loaded
 * lists (broken-URL profiles + duplicate groups), the initial-load lifecycle, the
 * last clean, the clean busy flag, the id of the group currently being merged inline, and
 * the last operation's notice. The confirm dialog's open flag stays local to the
 * page. Every field is serialisable.
 */
export interface ProfileCleaningState {
  brokenUrlProfiles: BrokenUrlProfile[];
  duplicateGroups: DuplicateGroup[];
  /** Profiles whose exits are in several currencies (#709): listed, never cleaned. */
  mixedCurrencyProfiles: MixedCurrencyExitProfile[];
  loadStatus: LoadStatus;
  /** The latest recorded clean (#619), `null` before the first one. */
  lastClean: LastClean | null;
  /**
   * Whether `lastClean` has been read at all. Kept apart from it because `null`
   * already means "never cleaned": until the read lands, or if it fails, the
   * band shows no line rather than a "Never" nobody has checked.
   */
  lastCleanLoaded: boolean;
  cleaning: boolean;
  mergingGroupId: string | null;
  /** The mixed-currency profile whose "Convert currencies" dialog is open (#732). */
  convertProfileId: string | null;
  /** A conversion is being saved: the dialog's Save waits. */
  converting: boolean;
  notice: Notice;
  /**
   * Bumped once each time a scoped clean settles. The page drops its ticked ids
   * and the DataTable drops its checkboxes off this: whatever the outcome, those
   * rows have been acted on and the list underneath has been refetched, so a
   * selection kept over them would point at rows that are no longer there.
   */
  selectionResetToken: number;
  /**
   * The page's "All issues" figure, unfiltered, behind the "Profile Cleaning"
   * sidebar badge (#753). `null` until first read: no badge rather than a 0
   * nobody has checked. Kept here rather than in the SideNav so the page's own
   * loads and actions can refresh it. The size of the "Exits & investments to
   * complete" queue comes with it, and is kept by the slice of its own page,
   * Exit & Investment Review (#762).
   */
  issueCount: number | null;
}

export const emptyNotice: Notice = { success: null, errors: [] };

export const initialState: ProfileCleaningState = {
  brokenUrlProfiles: [],
  duplicateGroups: [],
  mixedCurrencyProfiles: [],
  loadStatus: 'idle',
  lastClean: null,
  lastCleanLoaded: false,
  cleaning: false,
  mergingGroupId: null,
  convertProfileId: null,
  converting: false,
  notice: emptyNotice,
  selectionResetToken: 0,
  issueCount: null,
};
